import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { afterReceivingConversations, afterReceivingMessages, afterReceivingOneMessage } from './hooks'

const resource = 'chat'

export type ChatConversationType = 'dm' | 'group'

export type ChatMessageType = 'text' | 'gif' | 'poll' | 'system'

export type ChatEmbed = {
  type: 'resource' | 'candidate' | 'project' | 'evaluation' | 'process'
  entityId: string
  snapshot?: {
    title?: string
    subtitle?: string
    picture?: string | null
  }
}

export type ChatSender = {
  _id: string
  firstname: string
  lastname: string
  picture: string | null
}

export type ChatReactionGroup = {
  emoji: string
  count: number
  reactedByMe: boolean
  resourceIds: string[]
}

export type ChatReplyPreview = {
  _id: string
  body: string
  deletedAt?: Date | string | null
  sender?: ChatSender | null
  type?: ChatMessageType
}

export type ChatPollOption = {
  _id: string
  text: string
  voteCount: number
  percentage: number
}

export type ChatPoll = {
  _id: string
  question: string
  options: ChatPollOption[]
  /** Used to recompute viewer-specific flags after shared socket events */
  votes?: Array<{ resourceId: string; optionId: string }>
  totalVotes: number
  createdBy: string
  isAuthor: boolean
  hasVoted: boolean
  myOptionId: string | null
  showResults: boolean
  _created: Date | string
}

export type ChatAttachment = {
  _id: string
  fileName: string
  filePath: string | null
  fileDescription: string
  originalFileName: string
  fileType: string
  fileSize: number
  expiresAt: Date | string
  isExpired?: boolean
  uploadedBy: string
  status: 'pending' | 'linked' | 'purged'
  _created: Date | string
  expiresInDays?: number
  maxExpiresInDays?: number
  maxFileSizeBytes?: number
}

export type ChatMessage = {
  _id: string
  conversationId: string
  _firmId: string
  senderId: string
  body: string
  type: ChatMessageType
  embeds: ChatEmbed[]
  replyToMessageId?: string | null
  replyTo?: ChatReplyPreview | null
  mentionIds?: string[]
  /** Populated mentioned people (signed pictures) — rehydrated on every load */
  mentions?: ChatSender[]
  reactions?: ChatReactionGroup[]
  gif?: { provider: string; id: string; url: string; previewUrl?: string } | null
  pollId?: string | null
  poll?: ChatPoll | null
  attachmentIds?: string[]
  attachments?: ChatAttachment[]
  deletedAt?: Date | string | null
  editedAt?: Date | string | null
  _created: Date | string
  sender?: ChatSender | null
  seenBy?: ChatSender[]
}

export type ChatConversationListItem = {
  _id: string
  type: ChatConversationType
  title: string
  avatar: string | null
  lastMessageAt: Date | string | null
  lastMessagePreview: string
  lastMessageId: string | null
  unreadCount: number
  muted: boolean
  archived: boolean
  memberCount: number
  myRole: 'member' | 'admin'
  otherParticipant: ChatSender | null
}

export type ChatConversationDetail = {
  _id: string
  type: ChatConversationType
  title: string | null
  myRole: 'member' | 'admin'
  unreadCount: number
  muted: boolean
  members: Array<{
    resourceId: string
    role: 'member' | 'admin'
    joinedAt: Date | string
    firstname?: string
    lastname?: string
    picture?: string | null
  }>
}

export type CreateMessagePayload = {
  body?: string
  type?: ChatMessageType
  embeds?: ChatEmbed[]
  replyToMessageId?: string | null
  mentionIds?: string[]
  gif?: { provider: string; id: string; url: string; previewUrl?: string } | null
  poll?: { question: string; options: string[] } | null
  attachmentIds?: string[]
}

const listConversations = (axios: IFactoryFunctionRequest<ChatConversationListItem[], undefined>) => (query?: { archived?: boolean; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations`,
    query,
    afterReceivingHook: afterReceivingConversations,
  })
}

const createConversation = (axios: IFactoryFunctionRequest<any, { type: ChatConversationType; title?: string; memberIds?: string[] }>) => (body: { type: ChatConversationType; title?: string; memberIds?: string[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations`,
    data: body,
  })
}

const getConversation = (axios: IFactoryFunctionRequest<ChatConversationDetail, undefined>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${id}`,
  })
}

const updateConversation = (axios: IFactoryFunctionRequest<any, Record<string, unknown>>) => (id: string, body: Record<string, unknown>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${id}`,
    data: body,
  })
}

const leaveConversation = (axios: IFactoryFunctionRequest<{ ok: boolean }, undefined>) => (id: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${id}/leave`,
  })
}

const listMessages = (axios: IFactoryFunctionRequest<ChatMessage[], undefined>) => (conversationId: string, query?: { before?: string; limit?: number; q?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${conversationId}/messages`,
    query,
    afterReceivingHook: afterReceivingMessages,
  })
}

const createMessage = (axios: IFactoryFunctionRequest<ChatMessage, CreateMessagePayload>) => (conversationId: string, body: CreateMessagePayload) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${conversationId}/messages`,
    data: body,
    afterReceivingHook: afterReceivingOneMessage,
  })
}

const updateMessage = (axios: IFactoryFunctionRequest<ChatMessage, { body: string }>) => (messageId: string, body: { body: string }) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/messages/${messageId}`,
    data: body,
    afterReceivingHook: afterReceivingOneMessage,
  })
}

const deleteMessage = (axios: IFactoryFunctionRequest<ChatMessage, undefined>) => (messageId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/messages/${messageId}`,
    afterReceivingHook: afterReceivingOneMessage,
  })
}

const setReaction = (axios: IFactoryFunctionRequest<{ reactions: ChatReactionGroup[] }, { emoji: string }>) => (messageId: string, emoji: string) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/messages/${messageId}/reactions`,
    data: { emoji },
  })
}

const removeReaction = (axios: IFactoryFunctionRequest<{ reactions: ChatReactionGroup[] }, undefined>) => (messageId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/messages/${messageId}/reactions`,
  })
}

const votePoll = (axios: IFactoryFunctionRequest<ChatMessage, { optionId: string }>) => (messageId: string, optionId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/messages/${messageId}/poll/vote`,
    data: { optionId },
    afterReceivingHook: afterReceivingOneMessage,
  })
}

const uploadFile = (axios: IFactoryFunctionRequest<ChatAttachment, FormData>) => (
  conversationId: string,
  file: File,
  options?: { expiresInDays?: number; fileDescription?: string },
) => {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.expiresInDays != null) {
    formData.append('expiresInDays', String(options.expiresInDays))
  }
  if (options?.fileDescription) {
    formData.append('fileDescription', options.fileDescription)
  }
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${conversationId}/files`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const markRead = (axios: IFactoryFunctionRequest<{ ok: boolean; lastReadAt: string }, { lastReadMessageId?: string }>) => (conversationId: string, body?: { lastReadMessageId?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/conversations/${conversationId}/read`,
    data: body || {},
  })
}

const getUnreadCount = (axios: IFactoryFunctionRequest<{ unreadCount: number }, undefined>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/unread-count`,
  })
}

const searchGifs = (axios: IFactoryFunctionRequest<{ configured: boolean; results: Array<{ provider: string; id: string; url: string; previewUrl?: string; description?: string }>; message?: string }, undefined>) => (query?: { q?: string; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/gifs`,
    query,
  })
}

const unfurl = (axios: IFactoryFunctionRequest<{ embed: ChatEmbed | null }, { url: string }>) => (body: { url: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/unfurl`,
    data: body,
  })
}

const resolveEmbed = (axios: IFactoryFunctionRequest<{ embed: ChatEmbed | null }, { type: string; entityId: string }>) => (body: { type: string; entityId: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/embeds/resolve`,
    data: body,
  })
}

export default (axios: AxiosInstance) => ({
  listConversations: listConversations(useAxios(axios)),
  createConversation: createConversation(useAxios(axios)),
  getConversation: getConversation(useAxios(axios)),
  updateConversation: updateConversation(useAxios(axios)),
  leaveConversation: leaveConversation(useAxios(axios)),
  listMessages: listMessages(useAxios(axios)),
  createMessage: createMessage(useAxios(axios)),
  updateMessage: updateMessage(useAxios(axios)),
  deleteMessage: deleteMessage(useAxios(axios)),
  setReaction: setReaction(useAxios(axios)),
  removeReaction: removeReaction(useAxios(axios)),
  votePoll: votePoll(useAxios(axios)),
  uploadFile: uploadFile(useAxios(axios)),
  markRead: markRead(useAxios(axios)),
  getUnreadCount: getUnreadCount(useAxios(axios)),
  searchGifs: searchGifs(useAxios(axios)),
  unfurl: unfurl(useAxios(axios)),
  resolveEmbed: resolveEmbed(useAxios(axios)),
})
